import { useEffect, useRef } from 'react';

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal');
            els.forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  return (
    <section id="about" className="py-24 md:py-32 bg-stone-950 relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 md:px-10">
        {/* Section label */}
        <div
          className="reveal text-center mb-16"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold mb-4 border border-amber-500/20 tracking-widest uppercase">
            About the Author
          </span>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Author Photo */}
          <div
            className="reveal flex-shrink-0 flex flex-col items-center gap-6"
            style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            <div className="relative">
              <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-amber-400/30 to-amber-600/10 blur-xl"></div>
              <img
                src="https://storage.readdy-site.link/project_files/99887cdb-c324-46c8-9a26-303317ec92e6/66690b4f-31fe-4ebc-a3ed-6603650d6e18_kevin.jpg?v=d90a9d4c7a4704bb5723ce491fb4eb88"
                alt="Kevin Hamlett - Author"
                className="relative w-56 h-56 md:w-72 md:h-72 rounded-full object-cover object-top border-4 border-amber-500/30"
              />
            </div>
            {/* Social links */}
            <div className="flex items-center gap-3">
              {[
                { icon: 'ri-amazon-line', label: 'Amazon', href: 'https://www.amazon.com/s?k=Kevin+Hamlett&s=digital-text' },
                { icon: 'ri-twitter-x-line', label: 'Twitter', href: '#' },
                { icon: 'ri-facebook-line', label: 'Facebook', href: '#' },
                { icon: 'ri-instagram-line', label: 'Instagram', href: '#' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-stone-800 hover:bg-amber-500 text-stone-400 hover:text-stone-950 transition-all cursor-pointer"
                >
                  <i className={`${s.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Text */}
          <div className="flex-1">
            <h2
              className="reveal text-3xl md:text-5xl font-serif font-bold text-white mb-6 leading-tight"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              Meet{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
                Kevin Hamlett
              </span>
            </h2>

            <div
              className="reveal w-12 h-0.5 bg-amber-400 mb-8"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            ></div>

            <div
              className="reveal space-y-5 text-stone-300 text-base md:text-lg leading-relaxed"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              <p>
                Kevin Hamlett is a writer, thinker, and spiritual explorer whose work invites readers to look inward — to examine the currents of thought and feeling that shape who we are. His writing draws from the natural world, using rivers, light, and seasons as mirrors for the human soul.
              </p>
              <p>
                With a gift for lyrical prose and penetrating insight, Kevin crafts words that don't just inform — they transform. His books are meditations as much as they are narratives, designed to be read slowly, savored, and returned to again and again.
              </p>
              <p>
                Published by <strong className="text-amber-300">Leavitt Peak Press</strong>, Kevin's <em>Soul Line</em> series explores the intersection of consciousness, nature, and the deeper questions of human existence. <em>A River PENETRATING THE Soul Line: Book #II</em> is the latest chapter in this ongoing journey.
              </p>
            </div>

            {/* Highlights */}
            <div
              className="reveal mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              {[
                { icon: 'ri-book-2-line', label: 'Books Published', value: '2+' },
                { icon: 'ri-quill-pen-line', label: 'Writing Style', value: 'Lyrical Prose' },
                { icon: 'ri-heart-line', label: 'Genre', value: 'Spiritual / Soul' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4 p-4 rounded-xl bg-stone-900 border border-stone-800">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-500/10 flex-shrink-0">
                    <i className={`${item.icon} text-amber-400 text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-white font-bold text-base">{item.value}</div>
                    <div className="text-stone-500 text-xs">{item.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
